import { Match } from '@reach/router';
import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { IfUserHasPermission } from '../authorisation/IfUserHasPermission';
import { routes } from '../helpers/routes';
import { urls } from '../helpers/urls';
import { ReactComponent as PersonIcon } from '../images/icons/person.svg';
import { ReactComponent as Logo } from '../images/logo.svg';
import { LinkButton } from '../shared/Button';
import { Link } from '../shared/Link';
import { colourNeutral01, colourNeutral09, colourNeutral10, colourNeutral11 } from '../styling/colours';
import { fontSize01 } from '../styling/fonts';
import { spacing03, spacing05, spacing06, spacing07, spacing08, spacing09 } from '../styling/spacing';
import { UserContext } from '../users/UserContext';

type Props = {
  children?: React.ReactNode;
  logOut: () => void;
};

export const Layout = (props: Props) => {
  const user = useContext(UserContext).getUser();

  return (
    <>
      <Navbar>
        <LogoContainer to={urls.home}>
          <Logo />
        </LogoContainer>
        <NavLinks>
          <Match path={routes.projectSection}>
            {({ match }) => (
              <NavLinkContainer $active={window.location.pathname === '/' || match != null}>
                <NavLink to={urls.home}>Projects</NavLink>
              </NavLinkContainer>
            )}
          </Match>
          <Match path={routes.netSuiteSynchronisationInformation}>
            {({ match }) => (
              <NavLinkContainer $active={match != null}>
                <NavLink to={urls.netSuiteSynchronisationInformation}>NetSuite Sync Info</NavLink>
              </NavLinkContainer>
            )}
          </Match>
          <IfUserHasPermission requiredPermission="Manually Trigger Parts Update">
            <Match path={routes.documentSection}>
              {({ match }) => (
                <NavLinkContainer $active={match != null}>
                  <NavLink to={urls.documentList}>Document Management</NavLink>
                </NavLinkContainer>
              )}
            </Match>
          </IfUserHasPermission>
        </NavLinks>
        <UserActions>
          <UserName>
            <UserIcon />
            {user.name}
          </UserName>
          <LogOutButton onClick={props.logOut}>Log Out</LogOutButton>
        </UserActions>
      </Navbar>
      {props.children}
    </>
  );
};

const Navbar = styled.nav`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: ${spacing09};
  background-color: ${colourNeutral01};
  color: ${colourNeutral11};
  padding: 0 ${spacing05};
`;

const LogoContainer = styled(Link)`
  color: ${colourNeutral11};
  height: 50%;

  svg {
    height: 100%;
    width: auto;
  }
`;

const NavLinks = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: ${spacing08};
`;

const NavLinkContainer = styled.div<{ $active?: boolean }>`
  opacity: ${props => (props.$active ? 1 : 0.5)};

  &:not(:last-of-type) {
    margin-right: ${spacing06};
  }
`;

const NavLink = styled(Link)`
  color: ${colourNeutral10};
  font-size: ${fontSize01};
  text-transform: uppercase;
  letter-spacing: 1px;
  text-decoration: none;

  &:hover,
  &:focus {
    opacity: 1;
  }
`;

const UserActions = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const UserIcon = styled(PersonIcon)`
  height: 20px;
  margin-right: ${spacing03};
  position: relative;
  top: 1px;
`;

const UserName = styled.span`
  color: ${colourNeutral11};
  margin-right: ${spacing07};
  display: inline-flex;
  align-items: center;
`;

const LogOutButton = styled(LinkButton)`
  color: ${colourNeutral09};
  padding-left: 0;
  padding-right: 0;
`;

export const PageContainer = styled.div`
  padding: ${spacing06} ${spacing05};
  padding-bottom: ${spacing09};
`;
